/**
 * Aggregazione del costo di un preventivo a partire dalle sue righe.
 *
 * TRASLOCO, non riscrittura. La stessa formula era copiata IDENTICA in cinque
 * punti — la card "Vista vendita", la barra riepilogo, i due PDF di vendita e
 * il widget margine della dashboard — e qualunque correzione avrebbe dovuto
 * essere applicata cinque volte, con cinque occasioni di divergenza.
 *
 * I costi si leggono dalla FOTOGRAFIA congelata nella riga
 * (`stratigraphy_data`, esposta dalla query come `s.stratigraphy`), non dal
 * catalogo vivo: una riga di preventivo e` una copia, e il prezzo di vendita
 * deve nascere dagli stessi numeri che l'utente vede in tabella.
 */
import type { CostBase } from './applyPricing';
import { DEFAULT_WASTE_PCT } from '@/hooks/useWasteFactors';
import { PRICING_DEFAULTS } from '@/hooks/useOrgSettings';

/**
 * Le cinque chiamanti passano oggetti di forma diversa (righe mappate in
 * camelCase, righe grezze dal join della dashboard), quindi l'accesso resta
 * volutamente permissivo: e` esattamente cio` che facevano le cinque copie.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type CostRowLike = any;

/** Costo di UNA riga, gia` moltiplicato per la superficie. */
export interface EstimateRowCost {
  id?: string;
  area: number;
  /** Materiali + viti, PIU` il profilo perimetrale (€). */
  materials: number;
  /** Manodopera di posa, PIU` la maggiorazione per difficolta` (€). */
  labor: number;
  /** Finitura: materiali finitura + manodopera finitura (€). */
  finish: number;
  /** Solo la maggiorazione per difficolta`, gia` inclusa in `labor` (€). */
  difficultyExtra: number;
  /** Solo il profilo perimetrale, gia` incluso in `materials` (€). */
  perimeter: number;
  /** Solo lo sfrido, gia` incluso in `materials` (€). */
  waste: number;
  /** Solo lo smaltimento dei materiali, gia` incluso in `materials` (€). */
  disposal: number;
  /** Somma delle tre voci (€). */
  total: number;
}

function num(v: unknown, fb = 0): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : fb;
}

/**
 * Sfrido e smaltimento AL METRO QUADRO, calcolati layer per layer.
 *
 * Unico posto dove vive questa formula: la usano il calcolo del prezzo di
 * vendita, l'inserimento di una riga e "Aggiorna Prezzi". E` questo che rende
 * vera l'uguaglianza `area x unit_cost = costBase` — se le due strade
 * calcolassero lo sfrido in modo anche solo leggermente diverso, il costo
 * mostrato e quello usato per il prezzo tornerebbero a divergere.
 *
 * Priorita` per entrambe le percentuali: valore sul materiale (che la vista
 * `materials_with_pricing` restituisce gia` risolto con l'override della tua
 * organizzazione), poi il default — di categoria per lo sfrido, d'organizzazione
 * per lo smaltimento.
 *
 * Da non confondere con lo sfrido della RDA: quello determina le QUANTITA` da
 * ordinare al fornitore e arrotonda a lastre e scatole intere. Qui si calcola
 * solo il maggior costo in percentuale, la parte attribuibile al metro quadro.
 */
export function wasteAndDisposalPerSqm(
  layers: readonly CostRowLike[] | null | undefined,
  opts?: RowCostOptions,
): { waste: number; disposal: number } {
  if (!Array.isArray(layers)) return { waste: 0, disposal: 0 };
  const wasteMap = opts?.wasteMap;
  const disposalPct = num(opts?.disposalPct, 0);
  let waste = 0;
  let disposal = 0;
  const conta = (costo: number, mat: CostRowLike) => {
    if (!(costo > 0)) return;
    const category = String(mat?.category ?? '');

    const w = mat?.waste_percentage;
    const wPct = (w !== null && w !== undefined && Number.isFinite(Number(w)))
      ? Number(w)
      : num((wasteMap ?? DEFAULT_WASTE_PCT as Record<string, number>)[category], 0);
    const sfrido = wPct > 0 ? costo * (wPct / 100) : 0;
    waste += sfrido;

    // Lo smaltimento si calcola sul costo GONFIATO dello sfrido: si smaltiscono
    // anche i ritagli.
    const d = mat?.disposal_percentage;
    const dPct = (d !== null && d !== undefined && Number.isFinite(Number(d)))
      ? Number(d)
      : disposalPct;
    if (dPct > 0) disposal += (costo + sfrido) * (dPct / 100);
  };

  for (const l of layers) {
    conta(num(l?.material_cost_per_sqm), l?.materials ?? l?.material);
    // Anche le viti si sfridano e si smaltiscono: prima restavano fuori, e il
    // conteggio del preventivo copriva solo l'88% del costo materiali mentre
    // quello del configuratore le includeva — due numeri diversi per la stessa
    // parete.
    conta(num(l?.screw_cost_per_sqm), l?.screw_materials ?? l?.screwMaterial);
  }
  // I TASSELLI restano fuori da entrambi i conteggi: il loro costo non e`
  // memorizzato per layer (lo calcola il motore come aggregato), quindi non e`
  // raggiungibile dalla fotografia congelata. Vale ~0,01 €/m².
  return { waste, disposal };
}

function wasteEur(row: CostRowLike, area: number, wasteMap?: Record<string, number>): number {
  if (area <= 0) return 0;
  return wasteAndDisposalPerSqm(row?.layersData ?? row?.layers_data, { wasteMap }).waste * area;
}

/**
 * ATTENZIONE: `disposal_percentage` e` arrivato nella fotografia dei layer solo
 * dal 2026-08-28. Sulle righe congelate prima non c'e', e per quelle vale il
 * default dell'organizzazione — che oggi e` comunque il valore corretto, visto
 * che tutti i materiali avevano lo stesso 4%. Ricalcolando la riga il campo
 * entra e l'override diventa effettivo.
 */
function disposalEur(
  row: CostRowLike,
  area: number,
  defaultPct: number,
  wasteMap?: Record<string, number>,
): number {
  if (area <= 0) return 0;
  return wasteAndDisposalPerSqm(row?.layersData ?? row?.layers_data, {
    wasteMap,
    disposalPct: defaultPct,
  }).disposal * area;
}

/**
 * Manodopera della finitura in EURO al m².
 *
 * `finish_cost_per_sqm` = materiali finitura + manodopera finitura. I materiali
 * stanno, voce per voce, in `finish_components_data`: la differenza e` quindi la
 * sola manodopera, e si ottiene SENZA dover conoscere la tariffa oraria (che e`
 * un'impostazione globale non congelata nella riga).
 *
 * Verificato su tutte le 54 righe con finitura: la differenza coincide al
 * quarto decimale con `finish_labor_minutes_per_sqm / 60 x tariffa`.
 */
function finishLaborEurPerSqm(row: CostRowLike): number {
  const finishPerSqm = num(row?.finishCostPerSqm);
  if (finishPerSqm <= 0) return 0;
  const comps = row?.finishComponentsData;
  if (!Array.isArray(comps)) return finishPerSqm;
  let materialsPerSqm = 0;
  for (const c of comps) materialsPerSqm += num(c?.cost_per_sqm);
  return Math.max(0, finishPerSqm - materialsPerSqm);
}

export interface RowCostOptions {
  /**
   * Sfridi per categoria dell'organizzazione. Omesso = default di codice, che
   * oggi coincidono con quelli in tabella per tutte le org (verificato): nessuna
   * divergenza possibile fra schermo e PDF.
   */
  wasteMap?: Record<string, number>;
  /**
   * Smaltimento dei materiali, % sul loro costo. Omesso = nessuno smaltimento,
   * cosi` chi non passa nulla mantiene il comportamento precedente.
   */
  disposalPct?: number;
}

/**
 * Ricava le opzioni di costo dalle impostazioni di prezzo EFFETTIVE del
 * preventivo (org + override). Un solo posto che sa da dove arriva lo
 * smaltimento, cosi' card, barra, tabella e PDF non possono divergere.
 */
export function rowCostOptionsFrom(
  pricing: { disposal_materials_pct?: number } | null | undefined,
  wasteMap?: Record<string, number>,
): RowCostOptions {
  return {
    wasteMap,
    disposalPct: num(pricing?.disposal_materials_pct, PRICING_DEFAULTS.disposal_materials_pct),
  };
}

/**
 * Scompone il costo riga per riga. Serve a chi deve ripartire qualcosa sulle
 * singole stratigrafie (le colonne di ricavo, il prezzo per riga nei PDF):
 * la somma di queste righe e` per costruzione il `CostBase` qui sotto.
 */
export function computeEstimateRowCosts(
  rows: readonly CostRowLike[] | null | undefined,
  opts?: RowCostOptions,
): EstimateRowCost[] {
  const wasteMap = opts?.wasteMap;
  const disposalPct = num(opts?.disposalPct, 0);
  const out: EstimateRowCost[] = [];
  for (const s of rows ?? []) {
    const area = num(s?.area, 0);
    const inner = s?.stratigraphy ?? {};
    const baseMaterials = (num(inner.material_cost_per_sqm) + num(inner.screw_cost_per_sqm)) * area;
    const baseLabor = num(inner.labor_cost_per_sqm) * area;
    // Il `!= null` distingue "nessuna finitura" da "finitura a costo zero":
    // era cosi` in tutte e cinque le copie, e resta cosi`.
    const f = s?.finishCostPerSqm;
    const finish = f != null ? num(f) * area : 0;

    // MAGGIORAZIONE PER DIFFICOLTA`. Si applica alla sola manodopera — posa
    // piu` finitura — mai ai materiali: e` la stessa regola di
    // useBulkUpdateEstimateStratigraphyPrices e useUpdateEstimateStratigraphy.
    // Si RICALCOLA dai campi correnti invece di dedurla dal `total_cost`
    // congelato, che su alcune righe porta ancora una finitura vecchia.
    const difficulty = num(s?.difficultyMultiplier, 1) || 1;
    const laborPerSqm = num(inner.labor_cost_per_sqm) + finishLaborEurPerSqm(s);
    const difficultyExtra = Math.abs(difficulty - 1) < 1e-9
      ? 0
      : area * laborPerSqm * (difficulty - 1);

    // PROFILO PERIMETRALE dei controsoffitti: importo fisso per stanza, non
    // al m². E` un materiale (un profilo che compri), quindi va coi materiali.
    const perimeter = Math.max(0, num(s?.perimeterProfileCost));

    // SFRIDO. Compri piu` materiale di quello che posi: il ritaglio e` un costo
    // reale, e finora il preventivo lo ignorava mentre il Riepilogo Materiali
    // lo contava — su un preventivo reale erano 43.038 € di scarto fra le due
    // letture degli stessi materiali. Ora entra fra i costi diretti, come gia`
    // fanno difficolta` e perimetro. NON tocca le quantita` della RDA, che
    // restano il documento d'ordine al fornitore.
    const waste = wasteEur(s, area, wasteMap);

    // SMALTIMENTO dei materiali. Percentuale sul costo dei materiali (sfrido
    // compreso: smaltisci anche i ritagli). E` un costo di cantiere che prende
    // il ricarico materiali, distinto dal forfait "Smaltimento rifiuti" delle
    // Impostazioni, che resta un importo fisso sommato dopo i ricarichi.
    const disposal = disposalEur(s, area, disposalPct, wasteMap);

    const materials = baseMaterials + perimeter + waste + disposal;
    const labor = baseLabor + difficultyExtra;
    out.push({
      id: s?.id,
      area,
      materials,
      labor,
      finish,
      difficultyExtra,
      perimeter,
      waste,
      disposal,
      total: materials + labor + finish,
    });
  }
  return out;
}

/** Aggregato del preventivo, nella forma attesa da `applyPricing`. */
export function computeEstimateCostBase(
  rows: readonly CostRowLike[] | null | undefined,
  opts?: RowCostOptions,
): CostBase {
  let materials = 0;
  let labor = 0;
  let finish = 0;
  let totalArea = 0;
  let waste = 0;
  let disposal = 0;
  for (const r of computeEstimateRowCosts(rows, opts)) {
    materials += r.materials;
    labor += r.labor;
    finish += r.finish;
    totalArea += r.area;
    waste += r.waste;
    disposal += r.disposal;
  }
  return { materials, labor, finish, totalArea, waste, disposal };
}
